import { useCallback, useContext, useMemo, useRef, useSyncExternalStore } from "react"

import { ApplicationContext } from "../application/application-context"
import type { ViewStateUpdater, ViewStateValue } from "./view-state"

export function useViewStateService() {
  const application = useContext(ApplicationContext)
  if (application == null) {
    throw new Error("useViewStateService must be used within ApplicationProvider.")
  }
  return application.viewState
}

export function useViewState<T extends ViewStateValue>(key: string, initialValue: T) {
  const viewState = useViewStateService()
  const initialRef = useRef({ key, value: initialValue })
  if (initialRef.current.key !== key) {
    initialRef.current = { key, value: initialValue }
  }

  const subscribe = useCallback((listener: () => void) => viewState.subscribe(key, listener), [key, viewState])
  const getValue = useCallback(() => viewState.get(key, initialRef.current.value), [key, viewState])
  const value = useSyncExternalStore(subscribe, getValue, () => initialRef.current.value)
  const isRestored = useSyncExternalStore(
    subscribe,
    () => viewState.isRestored,
    () => false
  )
  const setValue = useCallback(
    (updater: ViewStateUpdater<T>) => {
      const current = viewState.get(key, initialRef.current.value)
      viewState.set(key, resolveViewStateUpdater(updater, current))
    },
    [key, viewState]
  )
  const reset = useCallback(() => viewState.remove(key), [key, viewState])
  const meta = useMemo(() => ({ isRestored, reset }), [isRestored, reset])

  return [value, setValue, meta] as const
}

function resolveViewStateUpdater<T extends ViewStateValue>(updater: ViewStateUpdater<T>, current: T) {
  return updater instanceof Function ? updater(current) : updater
}
